import React, { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { colors } from "../../../config/colors";
import TaskService from "../../../../services/TaskService";
import { toast } from "react-toastify";
import { getChargeOutRateTitle } from "../../../utils/jobTitleLabel";

const ViewTask = () => {
  const navigate = useNavigate();
  const chargeOutRateTitle = getChargeOutRateTitle();
  const location = useLocation();
  const task = location.state?.taskData;
  const [archiving, setArchiving] = useState(false);

  const handleClose = () => {
    navigate("/task-list");
  };

  const handleArchiveTask = async () => {
    try {
      setArchiving(true);
      const response = await TaskService.archiveTask(task._id, true);
      if (response.success) {
        toast.success("Task archived successfully");
        navigate("/task-list");
      } else {
        throw new Error("Failed to archive task");
      }
    } catch (error) {
      console.error("Error archiving task:", error);
      toast.error("Failed to archive task");
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchiveTask = async () => {
    try {
      setArchiving(true);
      const response = await TaskService.archiveTask(task._id, false);
      if (response.success) {
        toast.success("Task unarchived successfully");
        navigate("/task-list");
      } else {
        throw new Error("Failed to unarchive task");
      }
    } catch (error) {
      console.error("Error unarchiving task:", error);
      toast.error("Failed to unarchive task");
    } finally {
      setArchiving(false);
    }
  };

  if (!task) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> Task not found</span>
        </div>
      </div>
    );
  }

  const InfoCard = ({ title, children }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 transition-all duration-200 hover:shadow-md">
      <h4 className="text-lg font-semibold mb-4" style={{ color: colors.primary.headingsBody }}>
        {title}
      </h4>
      {children}
    </div>
  );

  const DataRow = ({ label, value, isHighlight = false }) => (
    <div className="flex flex-col space-y-1">
      <span className="text-sm font-medium text-gray-500">{label}</span>
      <span className={`text-sm ${isHighlight ? 'font-semibold' : 'text-gray-700'}`}>{value}</span>
    </div>
  );

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-primary-headings">Task Details</h2>
        <div className="flex gap-4">
          {!task.isArchived && (
            <button
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
              style={{ backgroundColor: colors?.warning?.dark || '#f59e0b' }}
              onClick={handleArchiveTask}
              disabled={archiving}
            >
              {archiving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                </svg>
              )}
              <span className="text-sm">{archiving ? 'Archiving...' : 'Archive Task'}</span>
            </button>
          )}
          {task.isArchived && (
            <button
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
              style={{ backgroundColor: colors.success.dark }}
              onClick={handleUnarchiveTask}
              disabled={archiving}
            >
              {archiving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              )}
              <span className="text-sm">{archiving ? 'Unarchiving...' : 'Unarchive Task'}</span>
            </button>
          )}
          {!task.isArchived && (
            <button
              className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
              style={{ backgroundColor: colors.highlights.callToAction }}
              onClick={() => navigate(`/edit-task/${task._id}`)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              <span className="text-sm">Edit Task</span>
            </button>
          )}
          <button
            className="inline-flex items-center px-4 py-2 text-white rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 shadow-sm whitespace-nowrap"
            style={{ backgroundColor: colors.highlights.callToAction }}
            onClick={handleClose}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            <span className="text-sm">Back to List</span>
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <InfoCard title="Task Information">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="col-span-2 md:col-span-1">
              <DataRow label="Task Title" value={task.title} isHighlight={true} />
            </div>
            <div>
              <DataRow label="Category" value={task.category?.categoryType || "Not Assigned"} />
            </div>
            {(task.cost && task.cost > 0) ? (
              <div>
                <DataRow label={chargeOutRateTitle} value={`$${task.cost.toLocaleString()}`} />
              </div>
            ) : null}
            <div>
              <DataRow
                label="Estimated Time"
                value={`${Math.floor(task.estimatedMinTime / 60)}h ${task.estimatedMinTime % 60}m (Min.)- ${Math.floor(task.estimatedMaxTime / 60)}h ${task.estimatedMaxTime % 60}m (Max.)`}
              />
            </div>
            {task.isArchived && (
              <div className="col-span-full">
                <DataRow label="Status" value="Archived" isHighlight={true} />
              </div>
            )}
          </div>
        </InfoCard>

        {task.description && (
          <InfoCard title="Description">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.description}</p>
          </InfoCard>
        )}
      </div>
    </div>
  );
};

export default ViewTask; 